import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canChangeStatus, WRITE_PERMISSION_DENIED } from '@/services/permissions';
import { getAgentSalespersonId } from '@/services/agent';
import { recordAudit } from '@/services/audit';

export interface ExchangeItem {
  id: string;
  direction: string;
  productName: string;
  sizeLabel: string;
  quantity: number;
  unitPrice: number;
}

export interface ExchangeRecord {
  id: string;
  exchangeNumber: string;
  customerName: string;
  customerCity: string;
  saleNumber: string;
  salespersonName: string;
  exchangeType: string;
  priceDifference: number;
  status: string;
  exchangeDate: string;
  reason: string;
  notes: string;
  items: ExchangeItem[];
}

export interface ExchangesResult {
  data: ExchangeRecord[];
}

export async function fetchExchanges(): Promise<ExchangesResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const [exchangesRes, itemsRes, salesRes, customersRes, variantsRes, productsRes, sizesRes, salespersonsRes] =
      await Promise.all([
        supabase.from('exchanges').select('*'),
        supabase.from('exchange_items').select('*'),
        supabase.from('sales').select('id, sale_number, customer_id, salesperson_id'),
        supabase.from('customers').select('id, first_name, last_name, city'),
        supabase.from('product_variants').select('*'),
        supabase.from('products').select('id, name'),
        supabase.from('sizes').select('*'),
        supabase.from('salespersons').select('id, full_name'),
      ]);

    if (exchangesRes.error) throw new Error(exchangesRes.error.message);
    if (itemsRes.error) throw new Error(itemsRes.error.message);
    if (salesRes.error) throw new Error(salesRes.error.message);
    if (customersRes.error) throw new Error(customersRes.error.message);
    if (variantsRes.error) throw new Error(variantsRes.error.message);
    if (productsRes.error) throw new Error(productsRes.error.message);
    if (sizesRes.error) throw new Error(sizesRes.error.message);

    const exchanges = (exchangesRes.data ?? []) as Array<Record<string, any>>;

    const saleMap = new Map(
      ((salesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s]),
    );
    const salespersonMap = new Map(
      ((salespersonsRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.full_name]),
    );

    // Filtro por agente: solo ve los cambios de las ventas que él facturó.
    const agentSalespersonId = await getAgentSalespersonId();
    const visibleExchanges = agentSalespersonId
      ? exchanges.filter((e) => saleMap.get(e.sale_id)?.salesperson_id === agentSalespersonId)
      : exchanges;
    const customerMap = new Map(
      ((customersRes.data ?? []) as Array<Record<string, any>>).map((c) => [c.id, c]),
    );
    const variantMap = new Map(
      ((variantsRes.data ?? []) as Array<Record<string, any>>).map((v) => [v.id, v]),
    );
    const productMap = new Map(
      ((productsRes.data ?? []) as Array<Record<string, any>>).map((p) => [p.id, p.name]),
    );
    const sizeMap = new Map(
      ((sizesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s]),
    );

    const itemsByExchange = new Map<string, ExchangeItem[]>();
    ((itemsRes.data ?? []) as Array<Record<string, any>>).forEach((ei) => {
      const variant = variantMap.get(ei.variant_id);
      const productName = variant ? productMap.get(variant.product_id) ?? '' : '';
      const size = variant ? sizeMap.get(variant.size_id) : undefined;
      const item: ExchangeItem = {
        id: ei.id,
        direction: String(ei.direction).toLowerCase(),
        productName,
        sizeLabel: size?.name ?? '',
        quantity: ei.quantity,
        unitPrice: Number(ei.unit_price),
      };
      const list = itemsByExchange.get(ei.exchange_id) ?? [];
      list.push(item);
      itemsByExchange.set(ei.exchange_id, list);
    });

    const data = visibleExchanges
      .map((e) => {
        const customer = customerMap.get(e.customer_id);
        const sale = saleMap.get(e.sale_id);
        return {
          id: e.id,
          exchangeNumber: e.exchange_number,
          customerName: customer
            ? `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim()
            : '',
          customerCity: customer?.city ?? '',
          saleNumber: sale?.sale_number ?? '',
          salespersonName: sale?.salesperson_id
            ? salespersonMap.get(sale.salesperson_id) ?? ''
            : '',
          exchangeType: e.exchange_type,
          priceDifference: Number(e.price_difference ?? 0),
          status: e.status,
          exchangeDate: e.created_at,
          reason: e.reason ?? '',
          notes: e.notes ?? '',
          items: itemsByExchange.get(e.id) ?? [],
        };
      })
      .sort((a, b) => (a.exchangeDate < b.exchangeDate ? 1 : -1));

    return { data };
  } catch (err) {
    console.error('[exchanges] Supabase connection failed:', err);
    throw err;
  }
}

// Entrada del formulario de cambio (crear).
export interface ExchangeInput {
  saleId: string;
  customerId: string;
  exchangeType: 'size_change' | 'product_change';
  outVariantId: string;
  inVariantId: string;
  quantity: number;
  priceDifference: number;
  reason: string;
}

export interface CreateExchangeResult {
  success: boolean;
  exchangeId?: string;
  exchangeNumber?: string;
  error?: string;
}

// Registra un cambio de postventa (talla o producto) en Supabase.
export async function createExchange(input: ExchangeInput): Promise<CreateExchangeResult> {
  if (!(await canWrite('exchanges'))) return { success: false, error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) {
    return { success: false, error: 'Supabase no está configurado.' };
  }

  const exchangeNumber = `C-${Date.now().toString().slice(-6)}`;

  try {
    const { data, error } = await supabase
      .from('exchanges')
      .insert({
        exchange_number: exchangeNumber,
        sale_id: input.saleId,
        customer_id: input.customerId,
        exchange_type: input.exchangeType,
        price_difference: input.priceDifference,
        status: 'pending',
        reason: input.reason.trim(),
      })
      .select('id');

    if (error) return { success: false, error: error.message };
    const exch = data?.[0] as { id: string } | undefined;
    if (!exch) return { success: false, error: 'No se pudo crear el cambio.' };

    recordAudit({
      entityType: 'exchanges',
      entityId: exch.id,
      action: 'CREATE',
      newValue: exchangeNumber,
    });

    // Capturar precios reales (venta) de las variantes saliente y entrante.
    const { data: exVariants } = await supabase
      .from('product_variants')
      .select('id, product_id')
      .in('id', [input.outVariantId, input.inVariantId]);
    const exProductIds = ((exVariants ?? []) as Array<Record<string, any>>).map((v) => v.product_id);
    const { data: exProducts } = await supabase
      .from('products')
      .select('id, retail_price')
      .in('id', exProductIds);
    const exPriceMap = new Map(
      ((exProducts ?? []) as Array<Record<string, any>>).map((p) => [p.id, Number(p.retail_price ?? 0)]),
    );
    const exVariantMap = new Map(
      ((exVariants ?? []) as Array<Record<string, any>>).map((v) => [v.id, v.product_id]),
    );
    const outPrice = exPriceMap.get(exVariantMap.get(input.outVariantId) ?? '') ?? 0;
    const inPrice = exPriceMap.get(exVariantMap.get(input.inVariantId) ?? '') ?? 0;

    const items = [
      { exchange_id: exch.id, variant_id: input.outVariantId, direction: 'out', quantity: input.quantity, unit_price: outPrice },
      { exchange_id: exch.id, variant_id: input.inVariantId, direction: 'in', quantity: input.quantity, unit_price: inPrice },
    ];
    const { error: itemsError } = await supabase.from('exchange_items').insert(items);
    if (itemsError) console.warn('[exchanges] exchange_items insert failed:', itemsError.message);

    return { success: true, exchangeId: exch.id, exchangeNumber };
  } catch (err) {
    console.warn('[exchanges] createExchange failed:', err);
    return { success: false, error: 'Error de conexión al crear el cambio.' };
  }
}

// Actualiza un cambio existente: estado (pending/completed/cancelled) y/o
// detalles (motivo y notas). Deja registro en auditoría.
export async function updateExchange(
  id: string,
  patch: { status?: string; reason?: string; notes?: string },
): Promise<{ error: string | null }> {
  // Cambio solo de estado (sin motivos/notas): lo permite también el rol de
  // operaciones. Editar detalles (motivo/notas) exige permiso `write`.
  const statusOnly =
    patch.status !== undefined && patch.reason === undefined && patch.notes === undefined;
  if (statusOnly) {
    if (!(await canChangeStatus('exchanges'))) return { error: WRITE_PERMISSION_DENIED };
  } else if (!(await canWrite('exchanges'))) {
    return { error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { data: current } = await supabase
    .from('exchanges')
    .select('status, reason, notes')
    .eq('id', id)
    .maybeSingle();
  const cur = (current ?? {}) as Record<string, any>;

  const payload: Record<string, any> = {};
  if (patch.status !== undefined) payload.status = patch.status;
  if (patch.reason !== undefined) payload.reason = patch.reason.trim() || null;
  if (patch.notes !== undefined) payload.notes = patch.notes.trim() || null;

  const { error } = await supabase.from('exchanges').update(payload).eq('id', id);
  if (error) return { error: error.message };

  // Al completar el cambio se aplica el efecto contable: movimientos de inventario
  // (EXCHANGE_OUT / EXCHANGE_IN) y ajuste monetario por diferencia de precio.
  if (patch.status === 'completed' && cur.status !== 'completed') {
    await applyExchangeEffect(id);
  }

  recordAudit({
    entityType: 'exchanges',
    entityId: id,
    action: 'UPDATE',
    fieldName: patch.status !== undefined ? 'status' : 'details',
    oldValue:
      patch.status !== undefined
        ? cur.status ?? ''
        : JSON.stringify({ reason: cur.reason, notes: cur.notes }),
    newValue:
      patch.status !== undefined
        ? patch.status
        : JSON.stringify({ reason: patch.reason, notes: patch.notes }),
  });
  return { error: null };
}

// Efecto contable de un cambio completado: reingresa el producto devuelto
// (EXCHANGE_OUT, available +X), descuenta el nuevo (EXCHANGE_IN, available -X)
// y materializa la diferencia de precio como un pago (positivo = cobro,
// negativo = reembolso), con regla anti-doble sobre exchange_id.
async function applyExchangeEffect(exchangeId: string): Promise<void> {
  const { data: exch } = await supabase
    .from('exchanges')
    .select('price_difference')
    .eq('id', exchangeId)
    .maybeSingle();
  if (!exch) return;

  // Idempotencia: si ya existe un ajuste monetario para este cambio, no repetir.
  const { data: existingPay } = await supabase
    .from('payments')
    .select('id')
    .eq('exchange_id', exchangeId)
    .maybeSingle();

  const { data: items } = await supabase
    .from('exchange_items')
    .select('variant_id, direction, quantity')
    .eq('exchange_id', exchangeId);

  for (const it of (items ?? []) as Array<Record<string, any>>) {
    const { data: inv } = await supabase
      .from('inventory')
      .select('available, reserved, consigned')
      .eq('variant_id', it.variant_id)
      .maybeSingle();
    const prev = Number(inv?.available ?? 0);
    const reserved = Number(inv?.reserved ?? 0);
    const consigned = Number(inv?.consigned ?? 0);
    const qty = Number(it.quantity);
    const dir = String(it.direction).toLowerCase();

    if (dir === 'out') {
      const next = prev + qty;
      await supabase
        .from('inventory')
        .update({ available: next })
        .eq('variant_id', it.variant_id);
      await supabase.from('inventory_movements').insert({
        variant_id: it.variant_id,
        movement_type: 'EXCHANGE_OUT',
        quantity: qty,
        prev_available: prev,
        new_available: next,
        prev_reserved: reserved,
        new_reserved: reserved,
        prev_consigned: consigned,
        new_consigned: consigned,
        unit_cost: 0,
        reason: 'Cambio - devolución de producto',
      });
    } else if (dir === 'in') {
      const next = Math.max(0, prev - qty);
      await supabase
        .from('inventory')
        .update({ available: next })
        .eq('variant_id', it.variant_id);
      await supabase.from('inventory_movements').insert({
        variant_id: it.variant_id,
        movement_type: 'EXCHANGE_IN',
        quantity: -qty,
        prev_available: prev,
        new_available: next,
        prev_reserved: reserved,
        new_reserved: reserved,
        prev_consigned: consigned,
        new_consigned: consigned,
        unit_cost: 0,
        reason: 'Cambio - entrega de producto nuevo',
      });
    }
  }

  // Ajuste monetario por diferencia de precio (anti-doble sobre exchange_id).
  const priceDifference = Number((exch as Record<string, any>).price_difference ?? 0);
  if (!existingPay && priceDifference !== 0) {
    await supabase.from('payments').insert({
      payment_number: `EXC-${Date.now().toString().slice(-8)}`,
      exchange_id: exchangeId,
      method: 'other',
      status: priceDifference > 0 ? 'approved' : 'refunded',
      amount: priceDifference,
      transaction_ref: null,
    });
  }
}